import api from "../../services/api";
import { formatPlannedMinutes, resolvePlannedMinutes } from "../../utils/plannedMinutes";

export function matchesStatusFilter(status, filter) {
  const key = String(status || "").trim().toLowerCase();
  const done = key === "afgerond" || key === "afgemeld" || key === "gesloten";
  if (filter === "open") return !done;
  if (filter === "active") return key === "in_uitvoering";
  if (filter === "paused") return key === "tijdelijk_stilgelegd";
  if (filter === "done") return done;
  return true;
}

export function toISODate(value) {
  if (!value) return "";
  if (typeof value === "string") {
    const raw = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return raw;
    }
    const dutchMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dutchMatch) {
      const [, day, month, year] = dutchMatch;
      return `${year}-${month}-${day}`;
    }
  }
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toValidDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function parseClock(value) {
  const raw = String(value || "").trim();
  const match = raw.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (!match) return null;
  return { hour: Number(match[1]), minute: Number(match[2]) };
}

export function resolveAgendaStartAt(item) {
  const explicitStart = toValidDate(item?.plannedStartAt);
  if (explicitStart) return explicitStart;

  const plannedDate = toValidDate(item?.plannedDate || item?.geplandeDatum);
  if (!plannedDate) return null;

  const clock = parseClock(item?.plannedStartTime || item?.startTime || item?.beginTijd);
  if (!clock) {
    plannedDate.setHours(0, 0, 0, 0);
    return plannedDate;
  }

  plannedDate.setHours(clock.hour, clock.minute, 0, 0);
  return plannedDate;
}

export function resolveAgendaEndAt(item, startAt = null) {
  const explicitEnd = toValidDate(item?.plannedEndAt);
  if (explicitEnd) return explicitEnd;

  const safeStart = startAt || resolveAgendaStartAt(item);
  if (!safeStart) return null;

  const clock = parseClock(item?.endTime || item?.eindTijd);
  if (clock) {
    const endAt = new Date(safeStart);
    endAt.setHours(clock.hour, clock.minute, 0, 0);
    if (endAt.getTime() <= safeStart.getTime()) {
      endAt.setDate(endAt.getDate() + 1);
    }
    return endAt;
  }

  const durationMinutes = Number(
    item?.plannedDurationMinutes ||
      item?.plannedMinutes ||
      item?.durationMinutes ||
      item?.duurMinuten ||
      0
  );
  if (durationMinutes > 0) {
    return new Date(safeStart.getTime() + durationMinutes * 60 * 1000);
  }

  return null;
}

export function agendaItemOverlapsDay(item, dayValue) {
  const dayStart = toValidDate(dayValue);
  if (!dayStart) return false;
  dayStart.setHours(0, 0, 0, 0);

  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayEnd.getDate() + 1);

  const startAt = resolveAgendaStartAt(item);
  const endAt = resolveAgendaEndAt(item, startAt);
  if (startAt && endAt && endAt.getTime() > startAt.getTime()) {
    return startAt.getTime() < dayEnd.getTime() && endAt.getTime() > dayStart.getTime();
  }

  if (startAt) {
    return toISODate(startAt) === toISODate(dayStart);
  }

  return toISODate(item?.plannedDate || item?.geplandeDatum) === toISODate(dayStart);
}

export function isTodayDate(value) {
  if (!value) return false;
  return toISODate(value) === toISODate(new Date());
}

export function getStatusDotClass(status) {
  const key = String(status || "").trim().toLowerCase();
  if (key === "in_uitvoering") return "bg-orange-500";
  if (key === "gepland") return "bg-blue-500";
  if (key === "tijdelijk_stilgelegd") return "bg-red-500";
  if (key === "afgerond" || key === "afgemeld" || key === "gesloten") return "bg-emerald-500";
  if (key === "gereed_voor_planning") return "bg-amber-500";
  return "bg-gray-400";
}

export function getWerkbonTitle(werkbon) {
  return (
    werkbon?.title ||
    werkbon?.titel ||
    werkbon?.omschrijving ||
    werkbon?.opdracht?.omschrijving ||
    werkbon?.werkbonNummer ||
    "Werkbon"
  );
}

export function toDateKey(value, timeZone) {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function getCurrentUserId(user) {
  return String(user?._id || user?.id || user?.userId || user?.sub || "");
}

export function resolveTodayDailyLog(werkbon, user, timeZone) {
  const todayKey = toDateKey(new Date(), timeZone);
  const userId = getCurrentUserId(user);
  if (!todayKey || !userId) return null;

  const list = Array.isArray(werkbon?.dailyLogs) ? werkbon.dailyLogs : [];
  const found = list.find(
    (entry) =>
      String(entry?.dateKey || "") === String(todayKey) &&
      String(entry?.user || "") === String(userId)
  );

  if (!found) return null;

  return {
    id: String(found?._id || ""),
    dateKey: String(found?.dateKey || todayKey),
    description: String(found?.description || ""),
    blocked: Boolean(found?.blocked),
    blockReason: String(found?.blockReason || ""),
  };
}

export function formatDatum(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL");
}

export function formatDatumTijd(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatPlannedWindow(werkbon) {
  if (!werkbon) return "";
  const dateLabel = formatDatum(werkbon.plannedDate);
  const startTime = String(werkbon?.plannedStartTime || "").trim();
  const durationLabel = formatPlannedMinutes(resolvePlannedMinutes(werkbon));

  let endTime = "";
  if (werkbon?.plannedEndAt) {
    const endDate = new Date(werkbon.plannedEndAt);
    if (!Number.isNaN(endDate.getTime())) {
      endTime = endDate.toLocaleTimeString("nl-NL", {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  }

  if (!dateLabel && !startTime && !endTime) return durationLabel;
  const timeLabel = [startTime, endTime].filter(Boolean).join(" - ");
  const baseLabel = [dateLabel, timeLabel].filter(Boolean).join(" ");
  return [baseLabel, durationLabel].filter(Boolean).join(" • ");
}

export function formatTimeRequestStatus(status) {
  const key = String(status || "").trim().toLowerCase();
  if (key === "approved") return "Goedgekeurd";
  if (key === "rejected") return "Afgewezen";
  return "In afwachting";
}

export function getTimeRequestStatusClassName(status) {
  const key = String(status || "").trim().toLowerCase();
  if (key === "approved") return "bg-green-50 text-green-700";
  if (key === "rejected") return "bg-red-50 text-red-700";
  return "bg-amber-50 text-amber-700";
}

export function normalizeWerkbonPhotos(werkbon) {
  const modern = Array.isArray(werkbon?.photos) ? werkbon.photos : [];
  const mappedModern = modern
    .map((photo, index) => ({
      id: String(photo?._id || `photo-${index}`),
      filename: String(photo?.filename || ""),
      url: resolvePhotoUrl(photo?.url || photo?.filename || ""),
      thumbUrl: resolvePhotoUrl(photo?.thumbnailUrl || photo?.thumbnailFilename || ""),
      category: String(photo?.category || "overig"),
      createdAt: photo?.createdAt || null,
    }))
    .filter((photo) => Boolean(photo.url));

  if (mappedModern.length > 0) {
    return mappedModern;
  }

  const legacy = Array.isArray(werkbon?.fotos) ? werkbon.fotos : [];
  return legacy
    .map((value, index) => ({
      id: `legacy-${index}`,
      url: resolvePhotoUrl(value),
      thumbUrl: resolvePhotoUrl(value),
      category: "overig",
      createdAt: null,
    }))
    .filter((photo) => Boolean(photo.url));
}

export function resolvePhotoUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;

  const apiOrigin = getApiOrigin();
  if (!apiOrigin) return raw;

  if (raw.startsWith("/uploads/")) {
    return `${apiOrigin}${raw}`;
  }

  if (raw.includes("/")) {
    return `${apiOrigin}/${raw.replace(/^\/+/, "")}`;
  }

  return `${apiOrigin}/uploads/werkbonnen/${encodeURIComponent(raw)}`;
}

export function getApiOrigin() {
  const baseUrl = String(api.defaults.baseURL || "").trim();
  if (!baseUrl) return "";
  return baseUrl.replace(/\/api\/?$/, "");
}

export function formatPhotoCategory(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "voor") return "Voor";
  if (key === "na") return "Na";
  if (key === "bewijs") return "Bewijs";
  return "Overig";
}

export function formatShortDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit" });
}

export function createLocalId() {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function revokePreviewUrls(items) {
  for (const item of items || []) {
    if (item?.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }
  }
}

let webpSupportCache = null;

export function supportsWebP() {
  if (webpSupportCache !== null) return webpSupportCache;
  try {
    const canvas = document.createElement("canvas");
    webpSupportCache = canvas.toDataURL("image/webp").startsWith("data:image/webp");
    return webpSupportCache;
  } catch {
    webpSupportCache = false;
    return false;
  }
}

export function getPreferredOutputMimeType(originalType) {
  if (supportsWebP()) return "image/webp";
  const normalized = String(originalType || "").toLowerCase();
  if (normalized === "image/png") return "image/png";
  return "image/jpeg";
}

export function withNewExtension(filename, extension) {
  const safe = String(filename || "foto").trim() || "foto";
  const dotIndex = safe.lastIndexOf(".");
  const baseName = dotIndex > 0 ? safe.slice(0, dotIndex) : safe;
  return `${baseName}.${extension}`;
}

export function blobToFile(blob, originalName, mimeType) {
  const extension = mimeType === "image/webp" ? "webp" : mimeType === "image/png" ? "png" : "jpg";
  const nextName = withNewExtension(originalName, extension);
  return new File([blob], nextName, {
    type: mimeType,
    lastModified: Date.now(),
  });
}

export function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Kon afbeelding niet laden"));
      image.src = String(reader.result || "");
    };
    reader.onerror = () => reject(new Error("Kon bestand niet lezen"));
    reader.readAsDataURL(file);
  });
}

export function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Kon afbeelding niet encoderen"));
        resolve(blob);
      },
      mimeType,
      quality
    );
  });
}

export async function compressImageFile(file, options = {}) {
  if (!(file instanceof File)) return file;
  if (!String(file.type || "").startsWith("image/")) return file;

  const maxWidth = Number(options.maxWidth || 1920);
  const quality = Number(options.quality || 0.8);
  const preferWebP = options.preferWebP !== false;
  const forceJpeg = options.forceJpeg === true;
  const targetMimeType = preferWebP
    ? getPreferredOutputMimeType(file.type)
    : forceJpeg
    ? "image/jpeg"
    : String(file.type || "image/jpeg");

  const image = await loadImageFromFile(file);
  const scale = image.width > maxWidth ? maxWidth / image.width : 1;
  const targetWidth = Math.max(1, Math.round(image.width * scale));
  const targetHeight = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) return file;

  ctx.drawImage(image, 0, 0, targetWidth, targetHeight);
  const blob = await canvasToBlob(canvas, targetMimeType, quality);
  const compressedFile = blobToFile(blob, file.name, targetMimeType);

  if (compressedFile.size >= file.size) {
    return file;
  }
  return compressedFile;
}

export async function editorDestToFile(dest, fallbackName = "foto.jpg") {
  if (dest instanceof File) {
    return dest;
  }

  if (dest instanceof Blob) {
    return new File([dest], fallbackName, {
      type: dest.type || "image/jpeg",
      lastModified: Date.now(),
    });
  }

  if (typeof dest === "string") {
    const blob = await fetch(dest).then((res) => res.blob());
    return new File([blob], fallbackName, {
      type: blob.type || "image/jpeg",
      lastModified: Date.now(),
    });
  }

  throw new Error("Onbekend editor-resultaat");
}

export function toDocumentUrl(raw) {
  const value = String(raw || "").trim();
  if (!value) return "#";
  if (/^https?:\/\//i.test(value)) return value;

  const base = String(api?.defaults?.baseURL || "").replace(/\/api\/?$/, "");
  if (!base) return value.startsWith("/") ? value : `/${value}`;
  return value.startsWith("/") ? `${base}${value}` : `${base}/${value}`;
}

export function shouldPreviewDocument(doc = {}) {
  const mimeType = String(doc?.mimeType || doc?.type || "").trim().toLowerCase();
  if (mimeType === "application/pdf" || mimeType.startsWith("image/")) {
    return true;
  }

  const previewExtensions = [
    "pdf",
    "png",
    "jpg",
    "jpeg",
    "gif",
    "webp",
    "bmp",
    "svg",
    "tif",
    "tiff",
  ];

  const candidates = [
    doc?.bestand,
    doc?.url,
    doc?.name,
    doc?.filename,
    doc?.title,
  ];

  return candidates.some((candidate) => {
    const fileRef = String(candidate || "")
      .trim()
      .toLowerCase()
      .split("#")[0]
      .split("?")[0];
    if (!fileRef) return false;
    const dotIndex = fileRef.lastIndexOf(".");
    const ext = dotIndex >= 0 ? fileRef.slice(dotIndex + 1) : "";
    return previewExtensions.includes(ext);
  });
}

export function toMoney(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0.00";
  return num.toFixed(2);
}
